import { InternalApiEntitySchema, InternalApiEntitySchemaField, InternalApiEntityType } from '../api-internal';
import { useQueryEntitySchema, useSortEntitySchema, useUpdateEntitySchema } from './entitySchemaActions';
import {
  SalvBanner,
  SalvBannerType,
  MenuItem,
  SalvButton,
  SalvMenuButton,
  SalvModal,
  SalvProgressCircle,
  SalvTable,
  Size,
} from '@salvapp/ui-components';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import EntitySchemaField from './EntitySchemaField';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useDispatch } from 'react-redux';
import Has from '../auth/Has';
import { CAN_MODIFY_SCHEMA } from '../auth/permissions';
import { showConfirmationModal } from '../confirmation-modal/confirmationModalReducer';
import { useMe } from '../organisation/useMe';
import { useCheckIfPersonsExist } from '../aml/personActions';
import { useCheckIfTransactionsExist } from '../aml/transactionActions';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { useQueryAttributeMappings } from './mappings/mappingsActions';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';
import { isChildField, useIndexAndSchemaAreOutOfSyncNotification } from './helper';

interface Props {
  entityType: InternalApiEntityType;
}

export const EntitySchema = ({ entityType }: Props) => {
  const {
    data: entitySchema,
    isPending: isEntitySchemaPending,
    isError: isEntitySchemaError,
    error: entitySchemaError,
  } = useQueryEntitySchema(entityType);
  const {
    data: attributeMappings,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
  } = useQueryAttributeMappings();

  const dispatch = useDispatch();
  const { permissions, features } = useMe();

  const canModifySchema = permissions.includes(CAN_MODIFY_SCHEMA);
  const getParentSchemaFields = (schema: InternalApiEntitySchema) =>
    schema.schemaFields.filter((schemaField) => !isChildField(schemaField));

  const getChildSchemaFields = (schema: InternalApiEntitySchema) =>
    schema.schemaFields.filter((schemaField) => isChildField(schemaField));

  const [showModal, setShowModal] = useState(false);
  const [schemaFieldInEditing, setSchemaFieldInEditing] = useState<InternalApiEntitySchemaField>();

  const { mutate: updateEntitySchema } = useUpdateEntitySchema();
  const { mutate: sortEntitySchema } = useSortEntitySchema();

  const { data: personsExist } = useCheckIfPersonsExist();
  const { data: transactionsExist } = useCheckIfTransactionsExist();
  const entitiesExist = !!(
    (entityType === InternalApiEntityType.PERSON && personsExist) ||
    (entityType === InternalApiEntityType.TRANSACTION && transactionsExist)
  );
  const { notify: notifyIndexAndSchemaAreOutOfSync } = useIndexAndSchemaAreOutOfSyncNotification();
  const [modalClass, setModalClass] = useState('non-object-field');

  const columnHelper = createColumnHelper<InternalApiEntitySchemaField>();
  const getColumns = useCallback(
    (schema: InternalApiEntitySchema) => [
      columnHelper.accessor('displayName', { header: 'Display name' }),
      columnHelper.accessor('fieldName', { header: 'Field name' }),
      columnHelper.accessor('type', {
        header: 'Type',
        cell: ({ getValue, row }) => (row.original.isMulti ? 'Array of ' : '') + getValue(),
      }),
      columnHelper.accessor('isNullable', {
        header: 'Required',
        cell: ({ getValue }) => (!getValue() ? 'Yes' : 'No'),
      }),
      columnHelper.accessor('searchable', {
        header: 'Searchable',
        cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
      }),
      columnHelper.accessor('hidden', {
        header: 'Visible',
        cell: ({ getValue }) => (!getValue() ? 'Yes' : 'No'),
      }),
      columnHelper.display({
        id: 'actions',
        size: 0,
        cell: ({ row }) => (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <SalvMenuButton
              size={Size.SMALL}
              placement={'bottom end'}
              ariaLabel={`Actions of field ${row.original.displayName}`}
              onAction={async ([action]) => {
                if (action === 'edit') {
                  setShowModal(true);
                  setSchemaFieldInEditing(schema.schemaFields.find((field) => field.id === row.original.id));
                } else if (action === 'delete') {
                  await dispatch(
                    showConfirmationModal({
                      title: 'Do you want to delete this schema field?',
                      confirmationText: 'Yes',
                      confirmButtonVariant: 'danger',
                      cancelText: 'No, keep it',
                      onConfirm() {
                        updateEntitySchema({
                          entityType,
                          schemaFields: schema.schemaFields.filter(
                            (field) => field.id !== row.original.id && field.parentId !== row.original.id
                          ),
                        });
                      },
                    })
                  );

                  if (entitiesExist && schema.schemaFields.find((field) => field.id === row.original.id)!.searchable) {
                    await notifyIndexAndSchemaAreOutOfSync();
                  }
                }
              }}
            >
              <MenuItem title={'Edit'} value={'edit'} />
              <MenuItem
                title={'Delete'}
                value={'delete'}
                disabled={
                  isFeatureEnabled(ORGANISATION_FEATURE.RESTRICT_SCHEMA_FIELD_CHANGES_FOR_SEARCH_FIELDS, features) &&
                  entitiesExist &&
                  (row.original.searchable || (row.original.features && row.original.features.length > 0))
                }
              />
            </SalvMenuButton>
          </div>
        ),
      }),
    ],
    [canModifySchema, entitiesExist]
  );

  const onColumnSort = useCallback(
    (sortedColumns: InternalApiEntitySchemaField[], schema: InternalApiEntitySchema) => {
      return sortEntitySchema({
        entityType,
        internalApiEntityOrder: [
          ...sortedColumns.map((schemaField, index) => {
            return {
              id: schemaField.id,
              order: index + 1,
            };
          }),
          ...(getChildSchemaFields(schema).map((schemaField) => {
            return {
              id: schemaField.id,
              order: schemaField.sortOrder,
            };
          }) ?? []),
        ],
      });
    },
    [entityType]
  );

  if (isEntitySchemaError || isAttributeMappingsError) {
    if (entitySchemaError?.status === 404) {
      return (
        <SalvBanner
          type={SalvBannerType.INFO}
          message={'Schema is currently being generated. Please try again after a few minutes.'}
        />
      );
    } else {
      const resources: string[] = [];
      if (isEntitySchemaError) {
        resources.push('Schema');
      }
      if (isAttributeMappingsError) {
        resources.push('Attribute Mappings');
      }
      return <DataLoadingErrorFallback message={'Could not load following resources: ' + resources.join(', ')} />;
    }
  } else if (isEntitySchemaPending || isAttributeMappingsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Entity Schema resources'} />;
  }

  const onHide = () => {
    setShowModal(false);
    setSchemaFieldInEditing(undefined);
  };
  return (
    <>
      <SalvTable
        title={'Schema fields'}
        columns={getColumns(entitySchema)}
        data={getParentSchemaFields(entitySchema)}
        columnVisibility={{
          actions: canModifySchema,
        }}
        striped
        emptyMessage={
          entityType === InternalApiEntityType.PERSON
            ? 'Person schema not defined yet'
            : 'Transaction schema not defined yet'
        }
        actions={
          <Has allPermissions={[CAN_MODIFY_SCHEMA]}>
            <SalvButton
              label='New field'
              icon={faPlus}
              onClick={() => setShowModal(true)}
              variant='primary'
              size={Size.SMALL}
            />
          </Has>
        }
        onManualSort={canModifySchema ? (sortedColumns) => onColumnSort(sortedColumns, entitySchema) : undefined}
      />
      <SalvModal show={showModal} onHide={onHide} className={`schema-field-modal ${modalClass}`}>
        <EntitySchemaField
          schemaField={schemaFieldInEditing}
          schema={entitySchema}
          attributeMappings={attributeMappings}
          onClose={onHide}
          setModalClass={setModalClass}
        />
      </SalvModal>
    </>
  );
};
